import { Link } from 'react-router-dom';
import { useCollection } from '@/hooks/useCollection';
import type { Ministry } from '@/lib/types';

const MINISTRY_ICONS = ['ri-user-heart-line', 'ri-heart-2-line', 'ri-building-line', 'ri-hand-heart-line'];

export default function ServicesPreview() {
  const { rows: ministries, loading, error, reload } = useCollection<Ministry>('ministries', 'name', true);

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">
              What We Offer
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
              Comprehensive Services for<br />Community Empowerment
            </h2>
            <div className="w-12 h-1 bg-accent-500 mt-5" />
          </div>
          <Link to="/services" className="btn-outline shrink-0">
            View All Services
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>

        {loading ? (
          <p className="text-center text-foreground-400 py-10">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading services...
          </p>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-red-600 text-sm mb-3">Could not load services.</p>
            <button type="button" onClick={reload} className="btn-outline">Retry</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ministries.map((service, idx) => (
              <article
                key={service.id}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all hover:-translate-y-1"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                      <i className={`${MINISTRY_ICONS[idx % MINISTRY_ICONS.length]} text-lg`} aria-hidden="true" />
                    </div>
                    <span className="text-3xl font-heading font-bold text-background-200">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-800 mb-2">{service.name}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed line-clamp-3">{service.description}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}